// Relire les formes d'avant le registre des champs : les champs des templates
// de custom nodes (short_text, boolean, image, select + isMulti…) et les
// valeurs qu'ils ont écrites. Les données déjà stockées restent lisibles ; la
// prochaine écriture les réécrit dans la forme du registre.

import { PILL_COLORS, type PillColor } from "../lib/colorPill";
import { coerceFieldValue, fieldDefSchema, readFieldValue } from "./registry";
import type { CoerceCtx, FieldDef, FieldValue, RowMeta } from "./types";

// Les conversions de relecture n'ont pas de lecteur : « aujourd'hui » n'y
// apparaît jamais (ce sont des valeurs déjà écrites), le fuseau ne sert qu'à
// ramener un instant à son jour.
const READ_CTX: CoerceCtx = { now: 0, timeZone: "UTC", locale: "en-US" };

/** Les noms de couleur des anciennes palettes, ramenés à celle des pastilles. */
const LEGACY_COLORS: Record<string, PillColor> = {
  gray: "grey",
  slate: "grey",
  zinc: "grey",
  neutral: "grey",
  stone: "grey",
  brown: "orange",
  amber: "yellow",
  emerald: "green",
  cyan: "teal",
  indigo: "blue",
  violet: "purple",
  fuchsia: "pink",
  rose: "red",
};

export function legacyColor(color: unknown): PillColor {
  if (typeof color !== "string") return "default";
  if ((PILL_COLORS as readonly string[]).includes(color)) return color as PillColor;
  return LEGACY_COLORS[color] ?? "default";
}

type LegacyField = {
  id?: unknown;
  name?: unknown;
  type?: unknown;
  required?: unknown;
  description?: unknown;
  options?: Record<string, unknown>;
  default?: unknown;
};

function legacyChoices(options: Record<string, unknown> | undefined) {
  const choices = Array.isArray(options?.choices) ? options.choices : [];
  return choices.flatMap((choice: unknown) => {
    const c = choice as { id?: unknown; label?: unknown; color?: unknown };
    return typeof c.id === "string" && typeof c.label === "string"
      ? [{ id: c.id, name: c.label, color: legacyColor(c.color) }]
      : [];
  });
}

function upgradeShape(field: LegacyField): Record<string, unknown> | null {
  const options = field.options ?? {};
  const base = {
    id: field.id,
    name: field.name,
    required: field.required,
    description: field.description,
  };
  switch (field.type) {
    case "short_text":
      return { ...base, type: "text", settings: { placeholder: options.placeholder } };
    case "number":
      return {
        ...base,
        type: "number",
        settings: { unit: options.unit, min: options.min, max: options.max },
      };
    case "date":
      return { ...base, type: "date", settings: {} };
    case "boolean":
      return { ...base, type: "checkbox", settings: {} };
    case "rich_text":
      return { ...base, type: "rich_text", settings: {} };
    case "image":
      return { ...base, type: "file", settings: { accept: ["image"], multiple: false } };
    case "select":
      return {
        ...base,
        type: options.isMulti === true ? "multi_select" : "select",
        settings: { options: legacyChoices(options) },
      };
    default:
      return null;
  }
}

/**
 * Une définition de champ stockée → un `FieldDef`, qu'elle soit déjà dans la
 * forme du registre ou dans l'ancienne. `null` : illisible, le champ est
 * ignoré plutôt que de casser le template entier.
 */
export function upgradeFieldDef(raw: unknown): FieldDef | null {
  if (!raw || typeof raw !== "object") return null;
  // L'ancienne forme se reconnaît à ses `options` sans `settings` : la tester
  // d'abord, car un ancien `select` passerait aussi le schéma actuel (clés
  // inconnues retirées)… en perdant ses options.
  const legacy = raw as LegacyField;
  const shape = "settings" in raw ? null : upgradeShape(legacy);
  if (!shape) {
    const current = fieldDefSchema.safeParse(raw);
    return current.success ? current.data : null;
  }
  const parsed = fieldDefSchema.safeParse(shape);
  if (!parsed.success) return null;
  const field = parsed.data;
  if (legacy.default === undefined) return field;
  const value = readFieldValueLenient(field, legacy.default);
  return value === null ? field : ({ ...field, default: value } as FieldDef);
}

export function upgradeFieldDefs(raw: unknown): FieldDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const field = upgradeFieldDef(item);
    return field ? [field] : [];
  });
}

/** Les anciennes formes de valeur que `coerce` ne sait pas lire seul. */
function legacyInput(field: FieldDef, stored: unknown): unknown {
  // Ancienne image : `{ url, key? }`, sans nom ni type.
  if (field.type === "file" && stored && typeof stored === "object" && !Array.isArray(stored)) {
    const image = stored as { url?: unknown; key?: unknown };
    if (typeof image.url === "string") {
      const name = image.url.split("/").pop()?.split("?")[0] || "image";
      return [
        {
          key: typeof image.key === "string" ? image.key : image.url,
          url: image.url,
          name,
          mime: "image/*",
          size: 0,
        },
      ];
    }
  }
  // Ancien lien de table : `{ href, pageTitle, pageImage, pageDescription }`.
  if (
    field.type === "url" &&
    stored &&
    typeof stored === "object" &&
    "href" in stored &&
    ("pageTitle" in stored || "pageImage" in stored || "pageDescription" in stored)
  ) {
    const link = stored as Record<string, unknown>;
    return {
      href: link.href,
      // Faute de métadonnées, l'ancienne grille titrait le lien par son URL.
      title: link.pageTitle !== link.href ? link.pageTitle : undefined,
      image: link.pageImage,
      description: link.pageDescription,
    };
  }
  return stored;
}

/**
 * Comme `readFieldValue`, mais une valeur d'une forme antérieure est
 * convertie plutôt qu'ignorée (un select stocké en liste d'un id, une
 * ancienne image…). Pour relire des données écrites avant le registre.
 */
export function readFieldValueLenient<T extends FieldDef>(
  field: T,
  stored: unknown,
  meta?: RowMeta,
): FieldValue | null {
  // Une ancienne forme d'abord : un ancien lien passerait la lecture stricte,
  // qui ignore ses clés inconnues — et perdrait son titre.
  const legacy = legacyInput(field, stored);
  const strict = legacy === stored ? readFieldValue(field, stored, meta) : null;
  if (strict !== null || stored === null || stored === undefined) return strict;
  const result = coerceFieldValue(field, legacy, READ_CTX);
  return result.ok ? result.value : null;
}
